import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

const StaticPageMetaData = () => {
  const { user, hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [formData, setFormData] = useState({
    slug: '',
    metaTitle: '',
    metaDescription: '',
    metaKeywords: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const canView = user?.role === 'admin' || hasPermission('web_settings', 'static_page_meta_data', 'view');
  const canAdd = user?.role === 'admin' || hasPermission('web_settings', 'static_page_meta_data', 'add');
  const canEdit = user?.role === 'admin' || hasPermission('web_settings', 'static_page_meta_data', 'edit');
  const canDelete = user?.role === 'admin' || hasPermission('web_settings', 'static_page_meta_data', 'delete');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.post('staticPageMetaList', {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search
      });
      setRows(response?.data || []);
      setTotalRecords(response?.recordsFiltered || 0);
    } catch (error) {
      toast.error('Failed to fetch static page metadata');
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, search]);

  useEffect(() => {
    const timeoutId = setTimeout(() => fetchData(), search ? 400 : 0);
    return () => clearTimeout(timeoutId);
  }, [fetchData, search]);

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  const createPage = async (e) => {
    e.preventDefault();
    if (!canAdd && !editingId) return;
    if (!canEdit && editingId) return;
    if (!formData.slug.trim()) {
      toast.error('Slug is required');
      return;
    }
    try {
      setSubmitting(true);
      const payload = editingId ? { ...formData, id: editingId } : formData;
      const response = await apiService.post('createStaticPageMeta', payload);
      if (response?.status) {
        toast.success(response.message || (editingId ? 'Updated successfully' : 'Created successfully'));
        setShowCreateModal(false);
        setEditingId('');
        setFormData({ slug: '', metaTitle: '', metaDescription: '', metaKeywords: '' });
        fetchData();
      } else {
        toast.error(response?.message || 'Failed to create');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  const openCreate = () => {
    setEditingId('');
    setFormData({ slug: '', metaTitle: '', metaDescription: '', metaKeywords: '' });
    setShowCreateModal(true);
  };

  const openEdit = (item) => {
    setEditingId(item._id);
    setFormData({
      slug: item.slug || '',
      metaTitle: item.metaTitle || '',
      metaDescription: item.metaDescription || '',
      metaKeywords: item.metaKeywords || ''
    });
    setShowCreateModal(true);
  };

  const deletePage = async (id) => {
    if (!canDelete) return;
    if (!window.confirm('Delete this static page meta?')) return;
    try {
      const response = await apiService.delete(`deleteStaticPageMeta/${id}`);
      if (response?.status) {
        toast.success(response.message || 'Deleted successfully');
        fetchData();
      } else {
        toast.error(response?.message || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (!canView) {
    return (
      <div className="p-0">
        <div className="bg-white rounded-lg shadow-sm p-6 text-sm text-gray-600">
          You do not have permission to view this page.
        </div>
      </div>
    );
  }

  return (
    <div className="p-0">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              placeholder="Search slug/title/description/keywords"
              className="w-full sm:w-[360px] px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            {canAdd && (
              <button
                onClick={openCreate}
                className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: '#009ef7' }}
              >
                <i className="bi bi-plus mr-1"></i>Create New Page
              </button>
            )}
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="py-10 text-center text-gray-500">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-gray-500">No static pages found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2 min-w-[180px]">Slug</th>
                    <th className="px-3 py-2 min-w-[220px]">Meta Title</th>
                    <th className="px-3 py-2 min-w-[260px]">Meta Description</th>
                    <th className="px-3 py-2 min-w-[220px]">Meta Keywords</th>
                    <th className="px-3 py-2 min-w-[140px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rows.map((item, index) => (
                    <tr key={item._id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                      <td className="px-3 py-2 font-semibold text-[#009ef7]">{item.slug}</td>
                      <td className="px-3 py-2">{item.metaTitle || '--'}</td>
                      <td className="px-3 py-2">{item.metaDescription || '--'}</td>
                      <td className="px-3 py-2">{item.metaKeywords || '--'}</td>
                      <td className="px-3 py-2">
                        {(canEdit || canDelete) ? (
                          <div className="flex items-center gap-2">
                            {canEdit && (
                              <button
                                onClick={() => openEdit(item)}
                                className="px-2 py-1 text-xs rounded bg-emerald-100 text-emerald-700"
                              >
                                Edit
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => deletePage(item._id)}
                                className="px-2 py-1 text-xs rounded bg-rose-100 text-rose-700"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        ) : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-xs sm:text-sm text-gray-700">
              Showing {(currentPage - 1) * recordsPerPage + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border rounded text-xs sm:text-sm disabled:opacity-50">Prev</button>
              <span className="text-xs sm:text-sm">Page {currentPage} / {totalPages || 1}</span>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages || 1, p + 1))} disabled={currentPage === (totalPages || 1)} className="px-3 py-1 border rounded text-xs sm:text-sm disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editingId ? 'Edit Static Page Meta' : 'Create Static Page Meta'}</h3>
              <button onClick={() => { setShowCreateModal(false); setEditingId(''); }} className="text-gray-500"><i className="bi bi-x-lg"></i></button>
            </div>
            <form onSubmit={createPage} className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm mb-1">Slug</label>
                <input type="text" value={formData.slug} onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
              </div>
              <div>
                <label className="block text-sm mb-1">Meta Title</label>
                <input type="text" value={formData.metaTitle} onChange={(e) => setFormData((prev) => ({ ...prev, metaTitle: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm mb-1">Meta Description</label>
                <textarea value={formData.metaDescription} onChange={(e) => setFormData((prev) => ({ ...prev, metaDescription: e.target.value }))} rows="3" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm mb-1">Meta Keywords</label>
                <input type="text" value={formData.metaKeywords} onChange={(e) => setFormData((prev) => ({ ...prev, metaKeywords: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => { setShowCreateModal(false); setEditingId(''); }} className="px-4 py-2 bg-gray-100 rounded-lg">Cancel</button>
                <button type="submit" disabled={submitting} className="px-4 py-2 text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#009ef7' }}>
                  {submitting ? (editingId ? 'Updating...' : 'Creating...') : (editingId ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaticPageMetaData;
